/**
 * Configurazione delle sezioni di primo livello della piattaforma.
 * Usata da: NavRail (rail icone colorate) e HomeView (landing unica post-login).
 * Mail e LinkedIn sono attive (CTA diretta nella sezione);
 * WhatsApp/Instagram/Facebook in lavorazione.
 */
import { Mail, type LucideIcon } from 'lucide-react';
import type { AppView } from '../store/ui';

// ---------------------------------------------------------------------------
// Loghi brand FULL-COLOR (colori ufficiali) per rail e Home.
// Le props extra (strokeWidth) vengono ignorate per compatibilità con lucide.
// ---------------------------------------------------------------------------
export type BrandIconProps = { size?: number; className?: string; strokeWidth?: number };

// Icona WhatsApp (glifo ufficiale, verde brand #25D366).
export function WhatsAppIcon({ size = 20, className }: BrandIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="#25D366"
      className={className}
      aria-hidden="true"
    >
      <path d="M12.04 2c-5.46 0-9.9 4.44-9.9 9.9 0 1.75.46 3.45 1.32 4.95L2 22l5.3-1.39a9.87 9.87 0 0 0 4.74 1.21h.01c5.46 0 9.9-4.44 9.9-9.9 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.11.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.24 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.17.24-.64.8-.78.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.1-.23-.16-.48-.29Z" />
    </svg>
  );
}

// Logo LinkedIn (quadrato blu #0A66C2 con "in" a giorno).
export function LinkedInLogo({ size = 20, className }: BrandIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="#0A66C2"
        d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
      />
    </svg>
  );
}

// Logo Instagram (glifo ufficiale con gradiente brand giallo→rosa→viola).
export function InstagramLogo({ size = 20, className }: BrandIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
      <defs>
        <linearGradient id="wm-ig-grad" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#FFD600" />
          <stop offset="0.35" stopColor="#FF7A00" />
          <stop offset="0.62" stopColor="#FF0069" />
          <stop offset="1" stopColor="#7638FA" />
        </linearGradient>
      </defs>
      <path
        fill="url(#wm-ig-grad)"
        d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"
      />
    </svg>
  );
}

// Logo Facebook (cerchio blu #1877F2 con "f" a giorno).
export function FacebookLogo({ size = 20, className }: BrandIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="#1877F2"
        d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
      />
    </svg>
  );
}

export type SectionKey = 'mail' | 'linkedin' | 'whatsapp' | 'instagram' | 'facebook';

export type SectionDef = {
  key: SectionKey;
  label: string;
  // Vista di destinazione della CTA "Entra" (diretta, senza pagine intermedie)
  target: AppView;
  // Colore brand della sezione (per loghi e accenti)
  color: string;
  icon: LucideIcon | ((props: BrandIconProps) => JSX.Element);
  active: boolean;
  // Immagine cinematica della sezione (servita da /public/sections)
  image: string;
  tagline: string;
  description: string;
  bullets: string[];
  roadmap?: string;
  // Badge opzionale mostrato accanto al titolo (es. "Beta release")
  badge?: string;
};

export const SECTIONS: SectionDef[] = [
  {
    key: 'mail',
    label: 'Mail',
    target: 'list',
    color: 'var(--portal-accent, #38BDF8)',
    icon: Mail,
    active: true,
    image: '/sections/mail.jpg',
    tagline: 'La tua posta, già organizzata.',
    description:
      "L'AI classifica, tagga e filtra i messaggi prima ancora che tu li apra. Tu decidi le regole, lei fa il lavoro.",
    bullets: [
      'Tag automatici per dominio e mittente',
      'Antispam con euristiche e AI',
      'Sync IMAP bidirezionale in tempo reale',
    ],
  },
  {
    key: 'linkedin',
    label: 'LinkedIn',
    target: 'linkedin',
    color: '#0A66C2',
    icon: LinkedInLogo,
    active: true,
    badge: 'Beta release',
    image: '/sections/linkedin.jpg',
    tagline: 'Il tuo network diventa un CRM.',
    description:
      'Importa i contatti LinkedIn e collegali alle conversazioni email, senza copia-incolla.',
    bullets: [
      "Import contatti con l'estensione Chrome",
      'Scheda cliente: profilo + storico email',
      'Note e follow-up in un click',
    ],
  },
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    target: 'home',
    color: '#25D366',
    icon: WhatsAppIcon,
    active: false,
    image: '/sections/whatsapp.jpg',
    tagline: 'Le chat aziendali accanto alla posta.',
    description:
      'Rispondi, archivia e cerca i messaggi dei clienti dallo stesso posto in cui gestisci le email.',
    bullets: [
      "Conversazioni clienti in un'unica vista",
      "Risposte assistite dall'AI",
      'Storico unificato con le email',
    ],
    roadmap: 'Tra le prossime sezioni ad arrivare',
  },
  {
    key: 'instagram',
    label: 'Instagram',
    target: 'home',
    color: '#DD2A7B',
    icon: InstagramLogo,
    active: false,
    image: '/sections/instagram.jpg',
    tagline: 'DM e commenti, senza cambiare app.',
    description: 'Gestisci la presenza Instagram del tuo brand direttamente dal portale.',
    bullets: ['DM e commenti in un feed unico', 'Suggerimenti di risposta AI', 'Assegnazione al team'],
    roadmap: 'In arrivo dopo WhatsApp',
  },
  {
    key: 'facebook',
    label: 'Facebook',
    target: 'home',
    color: '#1877F2',
    icon: FacebookLogo,
    active: false,
    image: '/sections/facebook.jpg',
    tagline: 'La pagina Facebook, sotto controllo.',
    description: 'Messenger e commenti della pagina arrivano dove già lavori.',
    bullets: ['Messenger integrato nel portale', "Commenti moderati con l'AI", 'Un solo archivio per tutti i canali'],
    roadmap: 'Nei prossimi rilasci',
  },
];
